// Thin REST client for the judicial-products-360 backend (see ../../backend and docs/API.md).
// Every function here maps 1:1 to a backend route. Dashboard binds these with the current
// JWT via createApi(token) so call sites read like `api.products.update(id, patch)`.

const BASE = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(status, message, code) { super(message); this.status = status; this.code = code; }
}

async function request(token, method, path, body) {
  const res = await fetch(`${BASE}/api${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return null;
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) throw new ApiError(res.status, data?.message || `خطأ (${res.status})`, data?.error);
  return data;
}

const qs = (params = {}) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (!entries.length) return "";
  return "?" + new URLSearchParams(entries).toString();
};

export function createApi(token) {
  const get = (path) => request(token, "GET", path);
  const post = (path, body) => request(token, "POST", path, body);
  const patch = (path, body) => request(token, "PATCH", path, body);
  const del = (path) => request(token, "DELETE", path);

  return {
    auth: {
      login: (email, password) => request(null, "POST", "/auth/login", { email, password }),
      logout: () => post("/auth/logout"),
    },
    me: () => get("/me"),
    updateProfile: (body) => patch("/me", body),
    changePassword: (currentPassword, newPassword) => post("/me/password", { currentPassword, newPassword }),

    products: {
      list: () => get("/products"),
      get: (id) => get(`/products/${id}`),
      create: (body) => post("/products", body),
      update: (id, body) => patch(`/products/${id}`, body),
      remove: (id) => del(`/products/${id}`),
      eligibleOwners: (id) => get(`/products/${id}/eligible-owners`),
    },

    indicators: {
      create: (productId, body) => post(`/products/${productId}/indicators`, body),
      update: (productId, indId, body) => patch(`/products/${productId}/indicators/${indId}`, body),
      remove: (productId, indId) => del(`/products/${productId}/indicators/${indId}`),
      addReading: (productId, indId, body) => post(`/products/${productId}/indicators/${indId}/readings`, body),
    },

    items: {
      create: (productId, sec, body) => post(`/products/${productId}/${sec}`, body),
      update: (productId, sec, itemId, body) => patch(`/products/${productId}/${sec}/${itemId}`, body),
      remove: (productId, sec, itemId) => del(`/products/${productId}/${sec}/${itemId}`),
      addStation: (productId, sec, itemId, body) => post(`/products/${productId}/${sec}/${itemId}/stations`, body),
      updateStation: (productId, sec, itemId, si, body) => patch(`/products/${productId}/${sec}/${itemId}/stations/${si}`, body),
      removeStation: (productId, sec, itemId, si) => del(`/products/${productId}/${sec}/${itemId}/stations/${si}`),
      reorderStations: (productId, sec, itemId, from, to) => post(`/products/${productId}/${sec}/${itemId}/stations/reorder`, { from, to }),
      addUpdate: (productId, sec, itemId, text) => post(`/products/${productId}/${sec}/${itemId}/updates`, { text }),
      removeUpdate: (productId, sec, itemId, ui) => del(`/products/${productId}/${sec}/${itemId}/updates/${ui}`),
      startImpact: (productId, itemId) => post(`/products/${productId}/devs/${itemId}/start-impact`),
    },

    impacts: {
      list: (productId) => get(`/products/${productId}/impacts`),
      setStatus: (productId, impId, closed) => patch(`/products/${productId}/impacts/${impId}/status`, { closed }),
    },

    cases: {
      list: (productId, params) => get(`/products/${productId}/cases${qs(params)}`),
      create: (productId, body) => post(`/products/${productId}/cases`, body),
      update: (productId, caseId, body) => patch(`/products/${productId}/cases/${caseId}`, body),
      remove: (productId, caseId) => del(`/products/${productId}/cases/${caseId}`),
      reply: (productId, caseId, text) => post(`/products/${productId}/cases/${caseId}/replies`, { text }),
      close: (productId, caseId) => post(`/products/${productId}/cases/${caseId}/close`),
      reopen: (productId, caseId) => post(`/products/${productId}/cases/${caseId}/reopen`),
    },

    users: {
      list: (params) => get(`/users${qs(params)}`),
      create: (body) => post("/users", body),
      update: (userId, body) => patch(`/users/${userId}`, body),
      setStatus: (userId, active) => patch(`/users/${userId}/status`, { active }),
      setProducts: (userId, productIds) => patch(`/users/${userId}/products`, { productIds }),
      remove: (userId) => del(`/users/${userId}`),
    },

    projectManagers: {
      list: (productId) => get(`/products/${productId}/project-managers`),
      candidates: (productId) => get(`/products/${productId}/pm-candidates`),
      assign: (productId, userId) => post(`/products/${productId}/project-managers`, { userId }),
      remove: (productId, userId) => del(`/products/${productId}/project-managers/${userId}`),
    },

    audit: {
      list: (params) => get(`/audit${qs(params)}`),
    },

    settings: {
      get: () => get("/settings"),
      update: (body) => patch("/settings", body),
    },
  };
}
